import React, { useState } from "react";
import { Divider, Flex, Grid, Input, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useUpdatePathPickOrder from "@/features/pathpick/features/order-history/hooks/mutation/useUpdatePathPickOrder";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickOrderDetailAutoCancelBox from "@/features/pathpick/features/order-history/features/detail/components/payment-section/PathPickOrderDetailAutoCancelBox";
import PathPickOrderDetailCard from "@/features/pathpick/features/order-history/features/detail/components/PathPickOrderDetailCard";
import PathPickOrderDetailInfoField from "@/features/pathpick/features/order-history/features/detail/components/PathPickOrderDetailInfoField";
import PathPickOrderDetailRefundAccount from "@/features/pathpick/features/order-history/features/detail/components/payment-section/PathPickOrderDetailRefundAccount";
import { ORDER_DETAIL_KEY } from "@/features/pathpick/features/order-history/features/detail/constants";
import { PathPickOrder } from "@/features/pathpick/features/order-history/types/response";
import { PathPickOrderDetailPreview } from "@/features/pathpick/features/order-history/features/detail/types";
import { selectPathPickOrderRemittanceView } from "@/features/pathpick/features/order-history/features/detail/utils";

interface Props {
  order: PathPickOrder;
  // 시작 가이드의 화면 미리보기 — 환불 계좌를 조회/저장하지 않고 목 값만 보여준다.
  preview?: PathPickOrderDetailPreview;
}

// 입금 정보 — 금액 영역의 형태(단일/3열)와 안내 문구는 selectPathPickOrderRemittanceView가
// 결정하고, 여기서는 분기 없이 그리기만 한다.
function PathPickOrderDetailRemittance({ order, preview }: Props) {
  const { t, tCurrencyString } = useI18nHelper();
  const remittance = selectPathPickOrderRemittanceView(order);
  const { amounts } = remittance;

  const price = (value: number) =>
    tCurrencyString(value, { type: "SYMBOL", code: order.currency });

  return (
    <PathPickOrderDetailCard title={t(`${ORDER_DETAIL_KEY}payment_title`)}>
      {amounts.kind === "single" ? (
        <Flex flexDirection={"column"} gap={"4px"}>
          <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
            {t(amounts.labelKey)}
          </Text>
          <Text textStyle={"body-lg-bold"} color={"#00417D"}>
            {price(amounts.amount)}
          </Text>
        </Flex>
      ) : (
        <Grid
          templateColumns={{
            base: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, 1fr)",
          }}
          gap={"16px"}
        >
          <RemittanceAmount
            label={t(`${ORDER_DETAIL_KEY}payment_amount`)}
            value={price(amounts.remittedAmount)}
            isEmphasized
          />
          <RemittanceAmount
            label={t(`${ORDER_DETAIL_KEY}final_amount`)}
            value={price(amounts.finalAmount)}
          />
          <RemittanceAmount
            label={t(`${ORDER_DETAIL_KEY}payment_amount_refund`)}
            value={price(amounts.refundAmount)}
          />
        </Grid>
      )}

      <Text
        textStyle={
          amounts.isNoteHighlighted ? "caption-bold" : "caption-medium"
        }
        color={amounts.isNoteHighlighted ? "#B27700" : "#8F8F8C"}
      >
        {t(amounts.noteKey)}
      </Text>

      <Divider borderColor={"#EFEFED"} />

      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={"16px"}>
        <PathPickOrderDetailInfoField
          label={t(`${ORDER_DETAIL_KEY}payment_due`)}
          value={remittance.dueAtLabel}
        />
        <PathPickOrderDetailInfoField
          label={t(`${ORDER_DETAIL_KEY}payment_account`)}
          value={remittance.account}
        />
        <PathPickOrderDetailInfoField
          label={t(`${ORDER_DETAIL_KEY}payment_holder`)}
          value={remittance.accountHolder}
        />
        <PathPickOrderDetailInfoField
          label={t(`${ORDER_DETAIL_KEY}payment_completed`)}
          value={remittance.completedAtLabel}
        />
        {/* 2열 그리드라 이 위치(5번째 칸)가 예금주 바로 아래다(모바일 1열에서는 그냥
            마지막 줄이다). 옛 주문은 null → "-". */}
        <RemitterNameField order={order} value={remittance.remitterName} />
      </Grid>

      {remittance.autoCancelDueAt && (
        <PathPickOrderDetailAutoCancelBox dueAt={remittance.autoCancelDueAt} />
      )}

      <Divider borderColor={"#EFEFED"} />

      <PathPickOrderDetailRefundAccount
        order={order}
        preview={preview?.refundAccount}
      />
    </PathPickOrderDetailCard>
  );
}

// 입금자명 — 어드민이 이 값으로 입금 내역을 대조하므로, 입금 확인 전까지는
// 바이어가 여기서 직접 고칠 수 있다(서버 게이트와 동일 조건). 확인 후엔 표시만 한다.
function RemitterNameField({
  order,
  value,
}: {
  order: PathPickOrder;
  value: string | null;
}) {
  const { t } = useI18nHelper();
  const { info } = useAppToasts({ id: "GLOBAL" });
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const { mutate: updateOrder, isPending: isSaving } = useUpdatePathPickOrder(
    order.id
  );

  const isEditable =
    order.status !== "CANCELED" && !order.remittance.completedAt;

  const startEdit = () => {
    setDraft(value ?? "");
    setIsEditing(true);
  };

  const save = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === (value ?? "")) {
      setIsEditing(false);
      return;
    }
    updateOrder(
      { remitterName: trimmed },
      {
        onSuccess: () => {
          setIsEditing(false);
          info(t(`${ORDER_DETAIL_KEY}payment_remitter_save_success`));
        },
      }
    );
  };

  return (
    <Flex flexDirection={"column"} gap={"2px"}>
      <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
        {t(`${ORDER_DETAIL_KEY}payment_remitter`)}
      </Text>
      {isEditing ? (
        <Flex
          gap={"6px"}
          alignItems={"center"}
          flexWrap={{ base: "wrap", md: "nowrap" }}
        >
          <Input
            value={draft}
            minWidth={0}
            maxLength={100}
            fontSize={"13px"}
            height={"32px"}
            borderRadius={"8px"}
            border={"0.5px solid #D4D4D1"}
            background={"#FCFCFB"}
            onChange={(event) => setDraft(event.target.value)}
          />
          <AppButton
            variant={"primary"}
            isDisabled={draft.trim().length === 0}
            isLoading={isSaving}
            onClick={save}
          >
            <Text textStyle={"caption-bold"}>
              {t(`${ORDER_DETAIL_KEY}payment_remitter_save`)}
            </Text>
          </AppButton>
          <AppButton
            variant={"white"}
            isDisabled={isSaving}
            onClick={() => setIsEditing(false)}
          >
            <Text textStyle={"caption-bold"}>{t("common:cancel")}</Text>
          </AppButton>
        </Flex>
      ) : (
        <Flex gap={"8px"} alignItems={"center"}>
          <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
            {value || "-"}
          </Text>
          {isEditable && (
            <Text
              as={"button"}
              textStyle={"caption-medium"}
              color={"#1272EF"}
              textDecoration={"underline"}
              onClick={startEdit}
            >
              {t(`${ORDER_DETAIL_KEY}payment_remitter_edit`)}
            </Text>
          )}
        </Flex>
      )}
    </Flex>
  );
}

function RemittanceAmount({
  label,
  value,
  isEmphasized,
}: {
  label: string;
  value: string;
  isEmphasized?: boolean;
}) {
  return (
    <Flex flexDirection={"column"} gap={"4px"}>
      <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
        {label}
      </Text>
      <Text
        textStyle={"body-lg-bold"}
        color={isEmphasized ? "#00417D" : "#1D1D1B"}
      >
        {value}
      </Text>
    </Flex>
  );
}

export default PathPickOrderDetailRemittance;
